import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc, collection, addDoc } from 'firebase/firestore';

import { useNavigate } from 'react-router-dom';
import { logout } from '../firebase/auth'; // Import logout function
import { useLocation } from 'react-router-dom';
import { getFunctions, httpsCallable } from "firebase/functions";

const DashboardEducatorThanks = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMsg, setPaymentMsg] = useState(""); // status for payment verification
  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();
  const { search } = useLocation();
  const hasSession = new URLSearchParams(search).has('session_id');
  const functions = getFunctions();
  const getCheckoutSession = httpsCallable(functions, "getCheckoutSession");


  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        try {
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        } catch (error) {
          console.error('Error fetching user data:', error.message);
        }
      }
      setLoading(false);
    };

    fetchUserData();
  }, [auth, db, ]);

  // If coming back from Stripe, verify session and save customContent
  useEffect(() => {
    if (!hasSession) return;

    (async () => {
      try {
        setPaymentMsg("Verifying payment…");

        const sessionId = new URLSearchParams(search).get("session_id");
        if (!sessionId) { setPaymentMsg("Missing session_id."); return; }

        // Fetch session data
        const res = await getCheckoutSession({ sessionId });
        const session = res.data; // { id, payment_status, ... }

        if (session?.payment_status !== "paid") {
          setPaymentMsg("Payment not completed yet.");
          return;
        }

        // grab draft from sessionStorage and store to Firestore
        const raw = sessionStorage.getItem("customContentDraft");
        if (!raw) { setPaymentMsg("No form draft found (session expired)."); return; }
        const draft = JSON.parse(raw);

        await addDoc(collection(db, "customContent"), {
          ...draft,
          stripeSessionId: session.id,
          paid: true,
          amount: session.amount_total,
          currency: session.currency,
          customerEmail: session.customer_email || draft.email || null,
          createdAt: new Date(),
        });

        sessionStorage.removeItem("customContentDraft");
        setPaymentMsg("Success! Your custom material request was submitted.");
      } catch (e) {
        console.error("", e);
        setPaymentMsg("");
      }
    })();
  }, [hasSession, search, db]);
  

  if (loading) return <p>Loading...</p>;
  if (!userData) return <p>No user data found.</p>;

  const handleSubjectClick = (subject) => {
    const [state, grade, view] = userData.role.split("-");
    const path = subject === "socialstudies" 
      ? `/educator/${state}/${state}-${grade}-${view}-socialstudies` 
      : `/educator/${state}/${state}-${grade}-${view}-${subject}`;
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error.message);
    }
  };


  const handleClassroomNavigation = () => {
    const [state, grade, view] = userData.role.split("-");
    const path = `/educator/${state}/${state}-${grade}-${view}-classroom`;
    navigate(path);
  };
  

  const subjects = ["science", "socialstudies", "math", "other"];
  const colors = ["#007bff", "#28a745", "#dc3545", "#ffc107"]; // Blue, Green, Red, Yellow
  const backgroundImageUrl = userData?.backgroundImageUrl || ""; // or hardcode a URL to test
  
  return (
    <div
  style={{
    ...styles.dashboardContainer,
    ...(backgroundImageUrl
      ? {
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      : {}),
  }}
>

      {/* Logo */}
      <div style={styles.logoContainer}>
        <img
          src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
          alt="Logo"
          style={styles.logo}
        />
      </div>

      {/* Center Container */}
      <div style={styles.centerContainer}>
     <h2>{userData.name || 'Educator'}</h2>
     <p>{`You are logged in as ${userData.email}`}</p>
     <h1>Thank you for your order!</h1>
     <h2>A team member will be in touch shortly.</h2>
     <p>Please check your email for updates (and your spam folder just in case).</p>
    {paymentMsg && (
      <div style={{ marginTop: 12, padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
        <strong>{paymentMsg}</strong>
      </div>
    )}
   </div>
    
      {/* Welcome Text */}
      <p style={styles.welcomeText}>
        Welcome to the Smart Board Learning Educator Membership platform. Choose your subject or access your classroom below!
      </p>

      {/* Subject Buttons */}
      <div style={styles.subjectButtonContainer}>
  {subjects.map((subject, index) => (
    <div
      key={subject}
      style={{
        ...styles.subjectButton,
        backgroundColor: colors[index % colors.length],
      }}
      onClick={() => handleSubjectClick(subject)}
    >
      <p style={styles.buttonText}>{subject.charAt(0).toUpperCase() + subject.slice(1)}</p>
    </div>
  ))}

  {/* Classroom Button (same style row, different color) */}
  <div
    style={{
      ...styles.subjectButton,
      ...styles.classroomButton, // Custom color override
    }}
    onClick={handleClassroomNavigation}
  >
    <p style={styles.buttonText}>Classroom</p>
  </div>
</div>

      {/* Admin Dashboard Link */}
      {userData?.roles?.includes('administrator') && (
        <div style={styles.adminLinkContainer}>
          <button style={styles.adminButton} onClick={() => navigate('/admin')}>
            Go to Admin Dashboard
          </button>
        </div>
      )}

      {/* Logout Button */}
      <button style={styles.logoutButton} onClick={handleLogout}>
        Logout
      </button>


      {/* Footer */}
      <footer style={styles.footer}>
        <p style={{ margin: 0 }}>© Smart Board Learning™</p>
        <p style={{ margin: 0 }}>All rights reserved.</p>
      </footer>
    </div>
  );
};


const styles = {
  dashboardContainer: {
    textAlign: 'center',
    padding: '20px',
    backgroundColor: '#e0f7ff',
    minHeight: '100vh',
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  logo: {
    height: '80px',
  },
  centerContainer: {
    backgroundColor: '#fff',
    borderRadius: '10px',
    padding: '20px',
    margin: '20px auto',
    maxWidth: '600px',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  },
  avatarContainer: {
    position: 'relative',
    marginBottom: '20px',
  },
  avatar: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  avatarButton: {
    position: "absolute",
    bottom: "0",
    right: "0",
    width: "60px",
    height: "60px",
    backgroundColor: "#16a085", // Teal
    color: "#fff",
    fontSize: "18px",
    border: "2px solid #222",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 4px 0 rgba(0, 0, 0, 0.3), 0 6px 8px rgba(0, 0, 0, 0.3)",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  
  subscriptionBadge: {
    backgroundColor: "#8e44ad", // Purple
    color: "#fff",
    fontWeight: "bold",
    fontSize: "16px",
    padding: "6px 12px",
    borderRadius: "6px",
    display: "inline-block",
    boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
    border: "2px solid #222",
    margin: "10px 0",
  },

  welcomeText: {
    marginTop: '30px',
    fontSize: '16px',
  },
  subjectButtonContainer: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "20px",
    marginTop: "20px",
  },
  
  subjectButton: {
    width: "175px",
    height: "175px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    borderRadius: "5px",
    color: "#fff",
    fontSize: "25px",
    fontWeight: "bold",
    textAlign: "center",
    padding: "10px 20px",
    border: "2px solid #222", // Adds depth
    boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)", // Layered shadow
    transition: "transform 0.2s, box-shadow 0.2s", // Smooth interaction
    backgroundColor: "#4a90e2", // Default to blue, can override with color variants below
  },
  
  buttonText: {
    color: "#fff",
  },

  scienceButton: {
    backgroundColor: "#4a90e2", // Blue
  },
  
  socialStudiesButton: {
    backgroundColor: "#4CAF50", // Green
  },
  
  mathButton: {
    backgroundColor: "#FF6347", // Reddish
  },
  
  otherButton: {
    backgroundColor: "#FFD700", // Yellow
  },
  
  homeButton: {
    backgroundColor: "#A020F0", // Purple
  },
  
  classroomContainer: {
    marginTop: '30px',
    textAlign: 'center',
  },
  classroomButton: {
    backgroundColor: "#00CED1", // Distinct turquoise color
    border: "2px solid #005a5a", // Darker border
    boxShadow: "0 6px 0 #005a5a, 0 8px 10px rgba(0,0,0,0.4)", // Custom shadow
  },
  
  adminLinkContainer: {
    marginTop: '40px',
    textAlign: 'center',
  },
  adminButton: {
    backgroundColor: '#007bff',
    color: 'white',
    padding: '10px 20px',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
  },
  logoutButton: {
    width: "200px",
    height: "75px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px 20px",
    fontSize: "22px",
    fontWeight: "bold",
    color: "#fff",
    backgroundColor: "#c0392b",
    border: "2px solid #222",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)",
    transition: "transform 0.2s, box-shadow 0.2s",
    marginTop: "40px", // 👈 space from subject buttons
  },
  footer: {
    marginTop: "40px",
    fontSize: "16px",
    color: "#555",
    textAlign: "center",
    padding: "10px",
    backgroundColor: "#f9f9f9",
    borderTop: "1px solid #ddd",
  },
};

export default DashboardEducatorThanks;
